/* eslint-disable react/prop-types */
import classNames from 'classnames';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { DATE_FORMAT } from 'constants/app.constant';
import { ErrorMessage } from 'components/common';
import 'react-day-picker/style.css';
import './dateView.scss';

const DEBOUNCE_MS = 500;

const isInvalidDate = (date: Date | string | undefined | null): boolean => {
  if (date == null) return true;
  const value = typeof date === 'string' ? new Date(date) : date;
  return Number.isNaN(value.getTime());
};

const defaultFormatDate = (date: Date, format = DATE_FORMAT) => (isInvalidDate(date) ? 'Invalid Date' : moment(date).format(format.toUpperCase()));

const isValidDateObject = (date?: Date): date is Date => !!date && !Number.isNaN(date.getTime());

const isOpenCalendarKey = (key: string) => key === 'ArrowDown' || key === ' ';

const extractYearFromInput = (rawValue: string): number | null => {
  const years = rawValue.match(/\d{4}/g);
  if (!years?.length) return null;
  const year = Number(years[years.length - 1]);
  return Number.isFinite(year) ? year : null;
};

export type DateViewTextFieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  'aria-label'?: string;
  'aria-required'?: boolean | 'true' | 'false';
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
};

export interface DateViewProps {
  id: string;
  children?: React.ReactNode;
  value?: string;
  format?: string;
  parseDate: (value: string, format: string) => Date | undefined;
  formatDate?: (date: Date, format: string) => string;
  handleDateChange?: (e?: React.ChangeEvent<HTMLInputElement> | undefined, value?: string) => void;
  handleKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  isDisabled?: boolean;
  disableDaysOfWeek?: number[];
  disableBefore?: Date;
  disableAfter?: Date;
  startMonth?: Date;
  endMonth?: Date;
  fieldClassName?: string;
  placeHolder?: string;
  iconLeft?: boolean;
  iconRight?: boolean;
  showErrorMessages?: boolean;
  errorMessages?: string[] | string;
  inputAriaLabel?: string;
  ariaRequired?: boolean;
  textFieldProps?: DateViewTextFieldProps;
}

interface UseDatePickerParams {
  value?: string;
  format?: string;
  parseDate: DateViewProps['parseDate'];
  formatDate?: DateViewProps['formatDate'];
  handleDateChange?: DateViewProps['handleDateChange'];
  handleKeyDown?: DateViewProps['handleKeyDown'];
  isDisabled?: boolean;
  textFieldOnBlur?: React.FocusEventHandler<HTMLInputElement>;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function useDatePicker(params: UseDatePickerParams) {
  const { value, format, parseDate, formatDate, handleDateChange, handleKeyDown, isDisabled, textFieldOnBlur } = params;

  const skipOpenOnFocusRef = useRef(false);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const calendarRef = useRef<HTMLDivElement | null>(null);
  const typingTimeoutRef = useRef<number | null>(null);

  const committedValue = value ?? '';
  const [localValue, setLocalValue] = useState(committedValue);

  const fmt = format || DATE_FORMAT;
  const selected = parseDate(committedValue, fmt);
  const selectedTime = isValidDateObject(selected) ? selected.getTime() : null;

  const [previewSelected, setPreviewSelected] = useState<Date | undefined>(undefined);
  const dayPickerSelected = previewSelected ?? (isValidDateObject(selected) ? selected : undefined);

  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState<Date>(() => (isValidDateObject(selected) ? selected : new Date()));

  const formatWith = useCallback(
    (date: Date) => {
      if (typeof formatDate === 'function') {
        return formatDate(date, fmt);
      }
      return defaultFormatDate(date, fmt);
    },
    [fmt, formatDate]
  );

  const setMonthIfChanged = useCallback((nextDate: Date) => {
    setMonth((prev) => (prev.getTime() === nextDate.getTime() ? prev : nextDate));
  }, []);

  const clearTypingTimeout = useCallback(() => {
    if (typingTimeoutRef.current != null) {
      window.clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  }, []);

  const closeCalendar = useCallback(() => {
    clearTypingTimeout();
    setOpen(false);
  }, [clearTypingTimeout]);

  const focusInputWithoutReopen = useCallback(() => {
    skipOpenOnFocusRef.current = true;
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const closeCalendarAndFocusInput = useCallback(() => {
    closeCalendar();
    focusInputWithoutReopen();
  }, [closeCalendar, focusInputWithoutReopen]);

  const notifyDateChange = useCallback(
    (date: Date) => {
      if (handleDateChange) {
        handleDateChange(undefined, formatWith(date));
      }
    },
    [formatWith, handleDateChange]
  );

  const commitDateSelection = useCallback(
    (date: Date) => {
      notifyDateChange(date);
      setMonthIfChanged(date);
      closeCalendarAndFocusInput();
    },
    [closeCalendarAndFocusInput, notifyDateChange, setMonthIfChanged]
  );

  const focusCalendarDay = useCallback(() => {
    window.requestAnimationFrame(() => {
      const root = calendarRef.current;
      if (!root) return;
      const target =
        root.querySelector<HTMLElement>('[aria-selected="true"]') ||
        root.querySelector<HTMLElement>('[tabindex="0"]') ||
        root.querySelector<HTMLElement>('.rdp-day_button[tabindex="0"]') ||
        root.querySelector<HTMLElement>('.rdp-day_button');
      if (target) {
        target.focus();
      }
    });
  }, []);

  const openCalendar = useCallback(() => {
    if (isDisabled) return;
    if (skipOpenOnFocusRef.current) {
      skipOpenOnFocusRef.current = false;
      return;
    }
    setOpen(true);
  }, [isDisabled]);

  const previewTypedDate = useCallback(
    (rawValue: string) => {
      if (!open) return;
      const parsed = parseDate(rawValue, fmt);
      if (isValidDateObject(parsed)) {
        setPreviewSelected(parsed);
        setMonthIfChanged(parsed);
        return;
      }
      setPreviewSelected(undefined);
      const extractedYear = extractYearFromInput(rawValue);
      if (extractedYear != null) {
        setMonthIfChanged(new Date(extractedYear, month.getMonth(), 1));
      }
    },
    [fmt, month, open, parseDate, setMonthIfChanged]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextRawValue = e.target.value;
      setLocalValue(nextRawValue);

      if (handleDateChange) {
        handleDateChange(e, nextRawValue);
      }

      clearTypingTimeout();
      typingTimeoutRef.current = window.setTimeout(() => {
        previewTypedDate(nextRawValue.trim());
      }, DEBOUNCE_MS);
    },
    [clearTypingTimeout, handleDateChange, previewTypedDate]
  );

  const handleInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (open) {
        const nextFocused = e.relatedTarget as Node | null;
        const root = wrapRef.current;
        if (root && nextFocused && !root.contains(nextFocused)) {
          closeCalendar();
        }
      }
      if (textFieldOnBlur) {
        textFieldOnBlur(e);
      }
    },
    [closeCalendar, open, textFieldOnBlur]
  );

  const handleEscapeKey = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): boolean => {
      if (e.key !== 'Escape') return false;
      closeCalendar();
      return true;
    },
    [closeCalendar]
  );

  const handleOpenKey = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): boolean => {
      if (open || !isOpenCalendarKey(e.key)) return false;
      openCalendar();
      e.preventDefault();
      return true;
    },
    [open, openCalendar]
  );

  const tryCommitTypedDate = useCallback(
    (rawValue: string): boolean => {
      const parsed = parseDate(rawValue.trim(), fmt);
      if (!isValidDateObject(parsed)) return false;
      commitDateSelection(parsed);
      return true;
    },
    [commitDateSelection, fmt, parseDate]
  );

  const handleEnterKey = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): boolean => {
      if (e.key !== 'Enter') return false;
      const raw = (e.target as HTMLInputElement).value ?? '';
      const didCommit = tryCommitTypedDate(raw);
      if (didCommit) {
        e.preventDefault();
      }
      return didCommit;
    },
    [tryCommitTypedDate]
  );

  const onInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (handleKeyDown) {
        handleKeyDown(e);
      }
      if (isDisabled) return;
      if (handleEscapeKey(e)) return;
      if (handleOpenKey(e)) return;
      handleEnterKey(e);
    },
    [handleEnterKey, handleEscapeKey, handleOpenKey, handleKeyDown, isDisabled]
  );

  const handleSelect = useCallback(
    (date?: Date) => {
      if (!isValidDateObject(date)) return;
      commitDateSelection(date);
    },
    [commitDateSelection]
  );

  const handleMonthChange = useCallback(
    (nextMonth: Date) => {
      setMonthIfChanged(nextMonth);
    },
    [setMonthIfChanged]
  );

  useEffect(() => {
    if (selectedTime == null) return;
    setMonth((prev) => {
      if (prev.getTime() === selectedTime) return prev;
      return new Date(selectedTime);
    });
  }, [selectedTime]);

  useEffect(() => {
    if (!open) {
      setPreviewSelected(undefined);
      setLocalValue(committedValue);
    }
  }, [open, committedValue]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current != null) {
        window.clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    focusCalendarDay();
  }, [focusCalendarDay, open, month]);

  useEffect(() => {
    if (!open) return undefined;

    const handleMouseDown = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      const target = e.target as Node | null;
      if (target && wrapRef.current.contains(target)) return;
      closeCalendar();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [closeCalendar, open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleFocusIn = (e: FocusEvent) => {
      const root = wrapRef.current;
      const target = e.target as Node | null;
      if (!root || !target) return;
      if (root.contains(target)) return;
      closeCalendar();
    };

    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, [closeCalendar, open]);

  useEffect(() => {
    const el = calendarRef.current;
    if (!open || !el) return undefined;
    const onEscapeKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeCalendarAndFocusInput();
      }
    };
    el.addEventListener('keydown', onEscapeKeyDown);
    return () => el.removeEventListener('keydown', onEscapeKeyDown);
  }, [open, closeCalendarAndFocusInput]);

  return {
    wrapRef,
    inputRef,
    calendarRef,
    localValue,
    dayPickerSelected,
    open,
    month,
    openCalendar,
    handleInputChange,
    handleInputBlur,
    onInputKeyDown,
    handleSelect,
    handleMonthChange
  };
}

const DateView: React.FC<DateViewProps> = ({
  id,
  children,
  value,
  format,
  parseDate,
  formatDate,
  handleDateChange,
  handleKeyDown,
  isDisabled,
  disableDaysOfWeek,
  disableBefore,
  disableAfter,
  startMonth,
  endMonth,
  fieldClassName,
  placeHolder,
  iconLeft,
  iconRight,
  showErrorMessages: showErrorMessagesProp,
  errorMessages,
  inputAriaLabel,
  ariaRequired,
  textFieldProps
}) => {
  const inputId = `field-input-${id}`;
  const popoverId = `${id}-datepicker-dialog`;

  const {
    wrapRef,
    inputRef,
    calendarRef,
    localValue,
    dayPickerSelected,
    open,
    month,
    openCalendar,
    handleInputChange,
    handleInputBlur,
    onInputKeyDown,
    handleSelect,
    handleMonthChange
  } = useDatePicker({
    value,
    format,
    parseDate,
    formatDate,
    handleDateChange,
    handleKeyDown,
    isDisabled,
    textFieldOnBlur: textFieldProps?.onBlur
  });

  const disabledDays = useMemo(
    () => ({
      daysOfWeek: disableDaysOfWeek,
      before: disableBefore,
      after: disableAfter
    }),
    [disableAfter, disableBefore, disableDaysOfWeek]
  );

  const showErrorMessages = !!(showErrorMessagesProp && errorMessages && errorMessages.length > 0);
  const accessibleLabel = (typeof children === 'string' && children.trim()) || inputAriaLabel || textFieldProps?.['aria-label'];

  return (
    <div id={`field-wrap-${id}`} className={classNames('c-els-field', fieldClassName)}>
      <label className="c-els-field__label" htmlFor={inputId}>
        {children != null && <span className="c-els-field__label-text">{children}</span>}

        <span
          ref={wrapRef}
          className={classNames('c-els-field__wrap', {
            'c-els-field__wrap--icon-left': iconLeft,
            'c-els-field__wrap--icon-right': iconRight
          })}
        >
          <svg role="img" className="o-els-icon-svg o-els-icon-svg--1x1o2 c-els-field__icon c-els-field__icon--right c-datepicker-icon" aria-hidden="true">
            <use xlinkHref="#icon-sprite_els-gizmo-icon-calendar" />
          </svg>

          <span className="c-datepicker-input">
            <input
              {...textFieldProps}
              ref={inputRef}
              id={inputId}
              className="c-els-field__input"
              placeholder={placeHolder}
              value={localValue}
              disabled={isDisabled}
              onFocus={openCalendar}
              onClick={openCalendar}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={onInputKeyDown}
              role="combobox"
              aria-haspopup="dialog"
              aria-expanded={open}
              aria-controls={popoverId}
              aria-label={accessibleLabel || 'Date input'}
              aria-required={ariaRequired ?? textFieldProps?.['aria-required']}
              autoComplete="off"
            />
          </span>

          {open && !isDisabled && (
            <div className="c-datepicker-wrapper">
              <div id={popoverId} ref={calendarRef} className={classNames('c-datepicker-overlay', 'date-calendar')} role="dialog" aria-modal="true">
                <DayPicker
                  mode="single"
                  month={month}
                  onMonthChange={handleMonthChange}
                  startMonth={startMonth}
                  endMonth={endMonth}
                  selected={dayPickerSelected}
                  required
                  autoFocus
                  disabled={disabledDays}
                  onSelect={handleSelect}
                />
              </div>
            </div>
          )}
        </span>
      </label>

      {showErrorMessages && <ErrorMessage key={`field-message-${id}`} message={errorMessages as string} hasError />}
    </div>
  );
};

export default DateView;
